import React, { useState, useRef, useEffect } from 'react';
import { Camera, Car, Heart, Leaf, Sparkles, Check, Eye, Compass } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import Leopard from '../assets/Leopard.webp';
import SelfDriveTour from '../assets/SelfDriveTour.webp';
import SustainableConservation from '../assets/SustainableConservation.webp';
import LuxStay from '../assets/LuxStay.jpg';
import BudgetStay from '../assets/BudgetStay.jpg';
import Tortoise from '../assets/Tortorise.webp';

interface TravelStyle {
	id: string;
	title: string;
	subtitle: string;
	image: string;
	icon: any;
	description: string;
	highlights: string[];
	ecoCertificate: string;
	links?: LinkImage[];
}

type LinkImage = {
	url: string;
	alt: string;
	logo: string;
};

const TRAVEL_STYLES: TravelStyle[] = [
	{
		id: 'luxury-stays',
		title: 'Luxury Stays',
		subtitle: 'Upscale accommodation in exclusive lodges and private reserves.',
		image: LuxStay,
		icon: Sparkles,
		description:
			'Immerse yourself in complete sanctuary. Settle into upscale accommodation in exclusive lodges and private reserves designed with precision detail to combine high-end European standard comforts with direct, uninterrupted nature access.',
		highlights: [
			'Upscale accommodation in exclusive lodges and private reserves.',
			'Guided tours to observe animals in exclusive reserves.',
			'Premium services like spa treatments and customised itineraries.',
		],
		ecoCertificate: 'Eco-Luxe Gold Alliance',
	},
	{
		id: 'budget-stays',
		title: 'Budget Stays',
		subtitle: 'More affordable options without compromising on experience.',
		image: BudgetStay,
		icon: Heart,
		description:
			'Experience the magic of the African bush closely on a more affordable tier. Settle into exceptional, charming lodges and guesthouses that prioritize high comfort, warmth, and raw authenticity.',
		highlights: [
			'More affordable options without compromising on experience.',
			'Exceptional accommodation, including charming lodges and guesthouses that offer comfort and quality.',
			'Guided safari tours, affordable options with all-inclusive or self-catering plans.',
		],
		ecoCertificate: 'Low-Footprint Green Standard',
	},
	{
		id: 'self-drive',
		title: 'Self-drive Tours',
		subtitle: 'For adventurous travelers who prefer to drive themselves.',
		image: SelfDriveTour,
		icon: Car,
		description:
			'The ultimate path of unscripted personal discovery. We provide completely well-planned itineraries, recommended routes, and pre-booked premium accommodation and activities to ensure you enjoy a completely hassle-free self-drive safari.',
		highlights: [
			'Well-planned itineraries, recommended routes, and pre-booked accommodation.',
			'Car rentals suggestions.',
			'Tips for navigating the terrain and exploring regions at your own perfect pace.',
		],
		ecoCertificate: 'Route Carbon-Compensation Offsets',
	},
	{
		id: 'big5',
		title: 'Big 5 Safari Tours',
		subtitle: 'Tailored safaris focused on spotting the legendary Big 5.',
		image: Leopard,
		icon: Camera,
		description:
			'Dedicated safaris curated to help you spot the legendary Big 5: Lion, Leopard, Elephant, Rhino, and Buffalo. Enjoy guided excursions led by top tracking specialists across South Africa’s most spectacular national parks.',
		highlights: [
			'Tailored safaris focusing on spotting the Big 5 (Lion, Leopard, Elephant, Rhino, and Buffalo).',
			'Focus on the "Big 5" animals with guided tours in stunning reserves and national parks.',
			'Phenomenal opportunities for tracking, photography, and educational bush experiences.',
		],
		ecoCertificate: 'Ranger Support Trust Subsidies',
	},
	{
		id: 'personalised-packages',
		title: 'Personalised Packages',
		subtitle: 'More Than Just a Safari Experience. Tailored to you.',
		image: Tortoise,
		icon: Compass,
		description:
			'Bespoke designs built to capture your personal definition of absolute wilderness adventure. Experience a real taste of Africa, whether you seek raw wildlife, scenic panoramas, rich culinary options, or deep cultural connections.',
		highlights: [
			'Tailored to your preferences: wildlife, scenic landscapes, city life, culinary or cultural steps.',
			'Activities like scenic flights, hippo cruises, whale watching, and deep fishing guides.',
			'Possibility of professional photographic guide accompaniment to capture your journey.',
		],
		ecoCertificate: 'Direct Local Community Wages',
	},
	{
		id: 'sustainable-interactions',
		title: 'Sustainable Interactions',
		subtitle: 'Conservation experiences. Immerse in efforts to preserve wildlife.',
		image: SustainableConservation,
		icon: Leaf,
		description:
			'Immerse yourself deeply in active conservation effort. Engage with hands-on, high-integrity wildlife frameworks that yield an intimate, profound understanding of South Africa’s preservation pledges.',
		highlights: [
			'Hands-on experiences for a deep understanding of preserving African fauna.',
			'May include participating in rhino dehorning efforts, pangolin walks, or monitoring sessions.',
			'Sustainable interactions like elephant encounters protecting creature well-being.',
		],
		ecoCertificate: '100% Ethical Certification Approved',
		links: [
			{
				alt: 'Wildlife ACT',
				url: 'https://wildlifeact.com/',
				logo: 'https://cdn.prod.website-files.com/655cbee32f7e5cfd123ab518/655e2445edc9d067f75af642_logo1.avif',
			},
			{
				alt: 'Zululand Conservation Trust',
				url: 'https://zululandconservationtrust.org/',
				logo: 'https://zululandconservationtrust.org/wp-content/uploads/2020/05/logo_hi-res.jpg',
			},
		],
	},
];

const getStyleKeys = (id: string) => {
	switch (id) {
		case 'luxury-stays':
			return {
				title: 'style_luxury_title',
				subtitle: 'style_luxury_subtitle',
				desc: 'style_luxury_desc',
				h1: 'style_luxury_h1',
				h2: 'style_luxury_h2',
				h3: 'style_luxury_h3',
				eco: 'style_luxury_eco',
			};
		case 'budget-stays':
			return {
				title: 'style_budget_title',
				subtitle: 'style_budget_subtitle',
				desc: 'style_budget_desc',
				h1: 'style_budget_h1',
				h2: 'style_budget_h2',
				h3: 'style_budget_h3',
				eco: 'style_budget_eco',
			};
		case 'self-drive':
			return {
				title: 'style_selfdrive_title',
				subtitle: 'style_selfdrive_subtitle',
				desc: 'style_selfdrive_desc',
				h1: 'style_selfdrive_h1',
				h2: 'style_selfdrive_h2',
				h3: 'style_selfdrive_h3',
				eco: 'style_selfdrive_eco',
			};
		case 'big5':
			return {
				title: 'style_big5_title',
				subtitle: 'style_big5_subtitle',
				desc: 'style_big5_desc',
				h1: 'style_big5_h1',
				h2: 'style_big5_h2',
				h3: 'style_big5_h3',
				eco: 'style_big5_eco',
			};
		case 'personalised-packages':
			return {
				title: 'style_personalized_title',
				subtitle: 'style_personalized_subtitle',
				tag: 'style_personalized_tag',
				desc: 'style_personalized_desc',
				h1: 'style_personalized_h1',
				h2: 'style_personalized_h2',
				h3: 'style_personalized_h3',
				eco: 'style_personalized_eco',
			};
		case 'sustainable-interactions':
			return {
				title: 'style_sustainable_title',
				subtitle: 'style_sustainable_subtitle',
				tag: 'style_sustainable_tag',
				desc: 'style_sustainable_desc',
				h1: 'style_sustainable_h1',
				h2: 'style_sustainable_h2',
				h3: 'style_sustainable_h3',
				eco: 'style_sustainable_eco',
			};
		default:
			return null;
	}
};

interface PackageGridProps {
	onSelectStyle: (styleId: string) => void;
}

export default function PackageGrid({ onSelectStyle }: PackageGridProps) {
	const [selectedStyle, setSelectedStyle] = useState<TravelStyle | null>(null);
	const [loadedStyles, setLoadedStyles] = useState<Record<string, boolean>>({});
	const { t } = useTranslation();
	const narrativeRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (selectedStyle && narrativeRef.current) {
			const timer = setTimeout(() => {
				narrativeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 80);
			return () => clearTimeout(timer);
		}
	}, [selectedStyle]);

	return (
		<section id='travel-styles-anchor' className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12'>
			{/* Visual Header */}
			<div className='text-center max-w-3xl mx-auto space-y-4'>
				<span className='font-mono text-[10px] tracking-widest text-brand-gold uppercase font-bold'>
					{t('ourServicesTab')}
				</span>
				<h2 className='font-serif text-3xl sm:text-4.5xl text-brand-forest tracking-tight font-medium animate-fade-in'>
					{t('seamlessEnrichingTitle')}
				</h2>
				<div className='w-12 h-0.5 bg-brand-gold mx-auto'></div>
				{/* <p className='font-sans text-sm text-brand-forest font-semibold leading-relaxed max-w-2xl mx-auto'>
					{t('seamlessEnrichingSubtitle1')}
				</p> */}
				<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light font-light leading-relaxed max-w-2xl mx-auto'>
					{t('seamlessEnrichingSubtitle2')}
				</p>
			</div>

			{/* Package Card Grid - Custom Soft Borders, Zoology Outlines, Hover Zooms */}
			<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
				{TRAVEL_STYLES.map(style => {
					const IconComponent = style.icon;
					const isImgLoaded = loadedStyles[style.id];
					const keys = getStyleKeys(style.id);
					const styleTitle = keys ? t(keys.title as any) : style.title;
					const styleSubtitle = keys ? t(keys.subtitle as any) : style.subtitle;

					return (
						<div
							key={style.id}
							className={`group flex flex-col justify-between overflow-hidden rounded-2xl bg-white transition-all duration-500 ease-in-out cursor-pointer text-left ${
								selectedStyle?.id === style.id
									? 'ring-2 ring-brand-gold shadow-xl scale-[1.02]'
									: 'shadow-lg hover:shadow-2xl hover:scale-[1.02]'
							}`}
							onClick={() => setSelectedStyle(selectedStyle?.id === style.id ? null : style)}
						>
							{/* Image with extreme Zoom Hover */}
							<div className='relative aspect-4/3 overflow-hidden select-none bg-earth-sand/20'>
								{/* Skeleton Shimmer */}
								{!isImgLoaded && (
									<div className='absolute inset-0 bg-linear-to-r from-earth-sand/10 via-earth-sand/35 to-earth-sand/10 animate-pulse flex items-center justify-center'>
										<Compass className='w-6 h-6 text-brand-gold/40 animate-spin [animation-duration:15s]' />
									</div>
								)}
								<img
									src={style.image}
									alt={styleTitle}
									loading='lazy'
									decoding='async'
									onLoad={() => setLoadedStyles(prev => ({ ...prev, [style.id]: true }))}
									className={`w-full h-full object-cover transition-all duration-700 ease-in-out group-hover:scale-110 ${
										isImgLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
									}`}
									referrerPolicy='no-referrer'
								/>

								{/* Soft gradient shadow overlay instead of border lines */}
								<div className='absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent'></div>

								{/* Pill tag indicating style category */}
								{/* <div className='absolute top-4 left-4'>
									<span className='bg-brand-forest-dark/85 backdrop-blur-md text-brand-gold font-sans text-[9px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full border border-brand-gold/25 block'>
										{styleCategoryTag}
									</span>
								</div> */}
							</div>

							{/* Text content with luxurious typography pairings */}
							<div className='p-6 space-y-4 flex-1 flex flex-col justify-between'>
								<div className='space-y-2'>
									<div className='flex items-center space-x-2 text-brand-forest'>
										<IconComponent className='w-4 h-4 text-brand-gold shrink-0' />
										<h3 className='font-serif text-base font-bold tracking-tight leading-tight group-hover:text-brand-gold-dark transition-colors duration-300 ease-in-out'>
											{styleTitle}
										</h3>
									</div>

									<p className='font-sans text-[11px] text-brand-charcoal-light font-light leading-relaxed'>
										{styleSubtitle}
									</p>
								</div>

								{/* Animated Call-to-readout indicator */}
								<div className='pt-3 border-t border-earth-sand/20 flex items-center justify-between'>
									<span className='text-[10px] font-sans font-semibold tracking-wider text-brand-gold-dark uppercase group-hover:text-brand-forest transition-colors duration-300 ease-in-out flex items-center space-x-1'>
										<span>{selectedStyle?.id === style.id ? t('hideNarrative') : t('readNarrative')}</span>
									</span>
									<div className='w-6 h-6 rounded-full bg-earth-cream group-hover:bg-brand-gold text-brand-forest group-hover:text-white transition-colors duration-300 ease-in-out flex items-center justify-center'>
										<Eye className='w-3.5 h-3.5' />
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{/* Dynamic Expansion narrative container */}
			{selectedStyle &&
				(() => {
					const keys = getStyleKeys(selectedStyle.id);
					const styleTitle = keys ? t(keys.title as any) : selectedStyle.title;
					const styleDesc = keys ? t(keys.desc as any) : selectedStyle.description;
					// const styleCategoryTag = keys ? t(keys.tag as any) : selectedStyle.categoryTag;
					const styleHighlights = keys
						? [t(keys.h1 as any), t(keys.h2 as any), t(keys.h3 as any)].filter(Boolean)
						: selectedStyle.highlights;
					const links = selectedStyle.links || [];

					return (
						<div
							id='expanded-narrative-content'
							ref={narrativeRef}
							className='bg-white rounded-2xl shadow-xl overflow-hidden border border-brand-gold/25 p-8 sm:p-10 animate-fade-in text-left relative scroll-mt-24 sm:scroll-mt-28'
						>
							<div className='absolute top-0 left-0 bg-brand-gold h-1.5 w-full'></div>

							<div className='grid grid-cols-1 lg:grid-cols-12 gap-8 items-center'>
								{/* Visual description */}
								<div className='lg:col-span-7 space-y-5'>
									<div className='flex items-center space-x-3'>
										<div className='p-2.5 rounded-lg bg-brand-forest/10 text-brand-forest'>
											{React.createElement(selectedStyle.icon, { className: 'w-6 h-6 text-brand-gold' })}
										</div>
										<div>
											{/* <span className='font-mono text-[9px] text-brand-gold tracking-[0.2em] block uppercase'>
												{styleCategoryTag}
											</span> */}
											<h3 className='font-serif text-2xl text-brand-forest font-bold leading-tight'>{styleTitle}</h3>
										</div>
									</div>

									<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light leading-relaxed font-light'>
										{styleDesc}
									</p>

									<div className='invisible inline-flex items-center space-x-2 bg-brand-forest/10 border border-brand-forest/15 rounded-lg px-3.5 py-2'>
										<span className='w-2 h-2 rounded-full bg-brand-forest animate-pulse'></span>
										<span className='font-sans text-[10px] sm:text-xs font-semibold text-brand-forest-dark uppercase tracking-wide'>
											Eco Blueprint
										</span>
									</div>

									{links?.length > 0 && (
										<div className='inline-flex  space-x-2 rounded-lg'>
											{links.map((link, index) => (
												<a
													key={index}
													href={link.url}
													target='_blank'
													rel='noopener noreferrer'
													className='flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-2 p-2 rounded-xl border border-earth-sand/35 hover:border-brand-gold/60 hover:shadow-md transition-all duration-200 group text-center sm:text-left'
												>
													<div className='h-12 w-28 shrink-0 flex items-center justify-center rounded-lg p-1'>
														<img
															src={link.logo}
															alt={link.alt}
															loading='lazy'
															decoding='async'
															className='max-h-full max-w-full object-contain'
															referrerPolicy='no-referrer'
														/>
													</div>
												</a>
											))}
										</div>
									)}
								</div>

								{/* highlights & action Column */}
								<div className='lg:col-span-12 xl:col-span-5 bg-earth-cream-dark rounded-xl p-6 border border-earth-sand/40 space-y-5'>
									<span className='font-sans text-[10.5px] tracking-widest text-brand-forest uppercase font-bold block pb-1 border-b border-earth-sand/40'>
										{t('pristineHighlights')}
									</span>

									<ul className='space-y-2.5 text-xs font-sans font-light text-brand-charcoal'>
										{styleHighlights.map((highlight, index) => (
											<li key={index} className='flex items-start space-x-2.5'>
												<Check className='w-4 h-4 text-brand-forest shrink-0 mt-0.5' />
												<span>{highlight}</span>
											</li>
										))}
									</ul>

									<div className='pt-2'>
										<button
											onClick={() => onSelectStyle(selectedStyle.id)}
											className='w-full bg-brand-forest hover:bg-brand-forest-light text-white font-sans text-xs tracking-widest uppercase font-bold py-3.5 rounded-lg transition-transform active:scale-98 shadow-sm cursor-pointer animate-fade-in'
										>
											{t('discoverOurPackages')}
										</button>
									</div>
								</div>
							</div>
						</div>
					);
				})()}

			{/* Elegant Layout Guarantee Tagline Section */}
			<div className='bg-earth-cream rounded-2xl border border-earth-sand/35 p-8 sm:p-12 text-center space-y-6 max-w-4xl mx-auto mt-16 shadow-xs'>
				{/* <span className='font-mono text-[9px] sm:text-[10px] tracking-[0.25em] text-brand-gold-dark font-extrabold uppercase'>
					{t('guaranteeHeader')}
				</span> */}
				<h3 className='font-serif text-2xl sm:text-3.5xl text-brand-forest font-medium tracking-tight'>
					{t('simplifyingJourneyTitle')}
				</h3>
				<div className='w-12 h-0.5 bg-brand-gold mx-auto'></div>
				<div className='max-w-2xl mx-auto space-y-4'>
					<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light font-light leading-relaxed'>
						{t('simplifyingJourneyDesc1')}
					</p>
					<p className='font-sans text-sm text-brand-forest leading-relaxed font-semibold'>
						{t('simplifyingJourneyDesc2')}
						<br />
						<span className='text-brand-gold-dark font-serif italic text-base sm:text-lg font-bold'>
							{t('neverForget')}
						</span>
					</p>
				</div>
			</div>
		</section>
	);
}
